import { X, Send, Mic, Volume2, VolumeOff } from "lucide-react";
import { FormEvent, useRef, useState } from "react";
import { useConfig } from "../contexts/config.context";
import { ChatMessage } from "./chat-message";
import { motion } from "framer-motion";
import { mergeClasses } from "../lib/mergeClasses";
import { ChatItem } from "./types";
import ThinkingBubbles from "./thinking-bubbles";

interface ChatInterfaceProps {
  isPTTActive?: boolean;
  isCallConnected?: boolean;
  isThinking?: boolean;
  onCallEnd: () => void;
  handleSubmitTextInput: (value: string) => void;
  chatHistory: ChatItem[];

  handlePTTStart: () => void;
  handlePTTEnd: () => void;
}

export function ChatInterface({
  onCallEnd,
  handleSubmitTextInput,
  isThinking,
  isPTTActive,
  handlePTTStart,
  handlePTTEnd,
  chatHistory,
}: ChatInterfaceProps) {
  const { config, updateConfig } = useConfig()

  const [textInput, setTextInput] = useState('');

  const lastMessageElRef = useRef<HTMLSpanElement>(null);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!textInput.trim()) return;
    handleSubmitTextInput(textInput);
    setTextInput('');
  };

  function getNamePrefix(name: string) {
    const parts = name.split(' ').filter(Boolean)
    if (parts.length > 2) {
      return parts.slice(0, 2).map(v => v[0]).join('')
    } else {
      return name.substring(0, 2)
    }
  }

  // useEffect(() => {
  //   function handleNewMessage(event: any) {
  //     log('handleNewMessage::config', config)
  //     setChatHistory(p => [...p, { role: event.role, text: event.text }])
  //     const tx = setTimeout(() => {
  //       lastMessageElRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
  //       clearTimeout(tx)
  //     }, 50)
  //   }
  //
  //   chatEventsEmitter.on('message', handleNewMessage)
  //
  //   return () => {
  //     chatEventsEmitter.off('message', handleNewMessage)
  //   }
  // }, [])

  function scrollToBottom() {
    if (lastMessageElRef.current) {
      lastMessageElRef.current.scrollIntoView({ behavior: "smooth", block: "end" })
    }
  }

  return (
    <motion.div
      id="chat-interface-container"
      initial={{ height: 300, opacity: 0.2 }}
      animate={{ height: 600, opacity: 1 }}
      exit={{ height: 300, opacity: 0.2 }}
    >
      {/* Header */}
      <div id="chat-interface-header" className="">
        <div className="flex items-center flex-1">
          <div id="chat-character-img">
            <img
              src={config.remote?.character.avatar || "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150"}
              alt={config.remote?.character.name || "AI Avatar"}
            />
          </div>
          <div className="ml-4">
            <h1 className="!text-white !font-semibold !text-xl !p-0 !m-0">{config.remote?.character.name || "Jenna"}</h1>
            {
              isThinking ? (
                <p className="!text-white !text-sm !p-0 !m-0">Thinking</p>
              ) : (
                <p className="!text-white !text-sm !p-0 !m-0">AI Assistant</p>
              )
            }
          </div>
        </div>

        {
          config.muted ? (
            <VolumeOff
              className={mergeClasses("w-4 h-4 !text-red-400 !cursor-pointer", {
                hidden: config.activeMode === 'chat'
              })}
              onClick={() => {
                updateConfig({ muted: false })
              }}
            />
          ) : (
            <Volume2
              className={mergeClasses("w-4 h-4 !text-white !cursor-pointer", {
                hidden: config.activeMode === 'chat'
              })}
              onClick={() => {
                updateConfig({ muted: true })
              }}
            />
          )
        }
        <X
          className="w-6 h-6 text-white cursor-pointer"
          onClick={() => onCallEnd()}
        />
      </div>

      {/* Chat Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {chatHistory.map((msg, index) => (
          <ChatMessage
            key={index}
            message={msg}
            isAI={msg.role !== 'user'}
            isAudio={false}
            AiName={config.remote?.character.name}
            // animate={index === chatHistory.length - 1}
            onAnimationEnd={() => {
              scrollToBottom()
            }}
          />
        ))}
        {
          isThinking && (
            <div className={`flex justify-start mb-4`}>
              <div className="w-8 h-8 !rounded-full !bg-[var(--sonaris-color-secondary)] flex items-center justify-center !mr-2">
                <span className="!text-white !text-sm !uppercase">{getNamePrefix(config.remote?.character.name || "Axx Intel")}</span>
              </div>
              <div className={mergeClasses('!rounded-lg !p-2 !bg-[var(--sonaris-color-primary)]/10')}>
                <ThinkingBubbles className="!w-full !bg-transparent" />
              </div>
            </div>
          )
        }
        <span ref={lastMessageElRef} />
      </div>

      {/* Input Area */}
      <div id="chat-interface-input-area">
        <button
          id="btn-ptt"
          type="button"
          className={mergeClasses("hover:!bg-[var(--sonaris-color-secondary)]/90", {
            '!hidden': config.activeMode === 'chat',
            '!scale-90': !!isPTTActive,
          })}
          onMouseDown={() => { handlePTTStart() }}
          onTouchStart={() => { handlePTTStart() }}
          onMouseUp={() => { handlePTTEnd() }}
          onTouchEnd={() => { handlePTTEnd() }}
        >
          <Mic className="w-5 h-5 !text-[var(--sonaris-color-text)]" />
        </button>
        <form
          onSubmit={handleSubmit}
          className={mergeClasses("flex items-center gap-2 overflow-clip transition-all", {
          })}>
          <div className="flex-1 bg-gray-100 rounded-full px-4 py-2">
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Type a message..."
              className="!m-0 !p-0 !h-auto !bg-none"
            />
          </div>
          <button
            type="submit"
            className={mergeClasses({
              '!border-none !bg-[var(--sonaris-color-secondary)] hover:!bg-[var(--sonaris-color-secondary)]/90 !text-white': config.activeMode === 'chat',
              '!border-2 !border-[var(--sonaris-color-secondary)] !bg-[var(--sonaris-color-primary)]/10 !hover:bg-[var(--sonaris-color-primary)]/30 !text-[var(--sonaris-color-secondary)]': config.activeMode === 'call',
            })}
          >
            <Send className="w-4 h-4 !text-inherit" />
          </button>
        </form>
      </div>

      {/* Powered by */}
      <div className="flex justify-center items-center text-xs text-black/30 my-1 mb-3">
        <span>Powered by</span>
        <span className="ml-1 font-medium flex items-center">
          <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3 mr-1">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M8 12h8M12 8v8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <a href="https://sonaris.ai" className="!underline !text-inherit" target="_blank">Sonaris.ai</a>
        </span>
      </div>
    </motion.div>
  );
}
